import { useCallback, useState } from 'react';
import { useFormHook, useSetError } from '@Hooks/index';
import {
  createNonUsHomeAddressSchema,
  HomeAddressFormScheme,
} from '@Pages/happyFlowPages/homeAddress/homeAddressForm/HomeAddressFormScheme.ts';
import { OnboardingFormFields } from '@Types/index.ts';
import { useUpdateCustomerHomeAddress } from '@Pages/happyFlowPages/homeAddress/hooks/useUpdateCustomerHomeAddress.tsx';
import { useBoundStore } from '@Store/index.ts';
import { syncSliceWithForm } from '@Pages/happyFlowPages/homeAddress/homeAddress.service.ts';
import { useAuthUserCompleteOnboarding } from '@Pages/inviteTeamMember/SetPassword/api/setPassword.api';
import { DocVStatus } from '@Store/slices/ubo/createUboSlice';

import { useNavigationManager } from '@Router/useNavigationManager';
import { getStateDropdownItem, nonUsRefineState } from '@Components/inputs/AddressStateDropDown';
import { nonUsRefineZip } from '@Components/inputs/ZipAddress';
import { useValidateAutoComplete } from '@Hooks/providers/useValidateAutoComplete';
import { useAvoidAutoComplete } from '@Hooks/custom/useAvoidAutoComplete.ts';
import { RoutesMap } from '@Router/router.types';
import { useNonUsCountryDefaultValues } from '../../../../hooks/custom/useNonUsCountryDefaultValues';
import { useSelectedCountryEffects } from './useSelectedCountryEffects';
import { useInitialFormTrigger } from './useInitialFormTrigger';
import type { IHomeAddressForm } from '@Types/homeAddress';
import type { ExtendedUseFormReturn } from '@Hooks/index';
import type { CombinedAddressForm } from '../../../../hooks/custom/useNonUsCountryDefaultValues';
import type { Country, CountryApiResponse } from '@Types/country';

export const useHomeAddressLogic = (
  countriesData: CountryApiResponse | undefined,
  originRoute: RoutesMap = RoutesMap.HOME_ADDRESS
) => {
  const { navigateToNextPage, navigateToSpecificPage } = useNavigationManager();
  useAvoidAutoComplete({
    idsToAvoidAutoComplete: [OnboardingFormFields.STATE, OnboardingFormFields.COUNTRY],
  });
  const isAuthUser = originRoute === RoutesMap.AUTH_USER_HOME_ADDRESS;
  const {
    customer: {
      customerData: { customerAddressData },
    },
    getIsUsFlow,
    getAuthUserIsUsFlow,
  } = useBoundStore((state) => state);
  const isUS = isAuthUser ? getAuthUserIsUsFlow() : getIsUsFlow();
  const { line1, line2, postalCode, state, city, country } = customerAddressData;
  const countryDecision =
    line1 &&
    postalCode &&
    state &&
    city &&
    line1 !== '' &&
    postalCode !== '' &&
    state !== '' &&
    city !== '' &&
    (!country || country === '')
      ? 'US'
      : country;
  const countryObject = countriesData?.countries.find(
    (countryItem) => countryItem.code === countryDecision
  );
  const [defaultValues, setDefaultValues] = useState<IHomeAddressForm>({
    [OnboardingFormFields.LINE1]: line1 || '',
    [OnboardingFormFields.APARTMENT]: line2 || '',
    [OnboardingFormFields.CITY]: city || '',
    [OnboardingFormFields.STATE]: getStateDropdownItem(state, countryObject) || null,
    [OnboardingFormFields.ZIP]: postalCode || '',
    [OnboardingFormFields.COUNTRY]: !isUS ? countryObject || undefined : undefined,
  });
  const [isDisableValidateAutoComplete, setIsDisableValidateAutoComplete] = useState(false);

  const countries = !isUS ? countriesData?.countries || [] : undefined;
  const nonUsSchema = createNonUsHomeAddressSchema();
  const finalNonUsSchema = nonUsRefineZip(nonUsRefineState(nonUsSchema));

  const formMethods: ExtendedUseFormReturn<IHomeAddressForm> = useFormHook({
    formSchema: isUS ? HomeAddressFormScheme : finalNonUsSchema,
    formDefaultValues: defaultValues,
    schemaTitle: 'homeAddress',
    values: defaultValues,
  });
  useSetError({ formMethods });

  useInitialFormTrigger({
    formMethods,
    addressFields: {
      line1,
      line2,
      postalCode,
      state,
      city,
      country,
    },
  });

  const setAuthUserDocVData = useBoundStore((s) => s.setAuthUserDocVData);
  const { mutate: authUserCompleteOnboarding, isPending: isCompletingOnboarding } =
    useAuthUserCompleteOnboarding({
      onSuccess: (response) => {
        if (response.docvRequired) {
          setAuthUserDocVData({
            status: DocVStatus.PENDING,
            url: response.url,
            qrCode: response.qrCode,
          });
          navigateToSpecificPage(RoutesMap.AUTH_USER_DOCV);
        } else {
          navigateToSpecificPage(RoutesMap.INVITE_TEAM_MEMBER_FINISHED);
        }
      },
    });

  const { mutateAsync: asyncUpdateHomeAddress, isPending } = useUpdateCustomerHomeAddress(() => {
    syncSliceWithForm(formMethods.getValues());
    if (isAuthUser) {
      authUserCompleteOnboarding();
    } else {
      navigateToNextPage(originRoute);
    }
  });

  const handleSubmit = useCallback(async () => {
    const formData = formMethods.getValues();
    await asyncUpdateHomeAddress(formData);
  }, [formMethods, asyncUpdateHomeAddress]);

  const { watch, clearErrors, resetField, setValue } = formMethods;
  const selectedCountry: Country | undefined | null = watch(OnboardingFormFields.COUNTRY);
  const usCountry = isUS ? countriesData?.countries.find((c) => c.code === 'US') : undefined;
  const subDivisions = isUS ? usCountry?.subDivisions : selectedCountry?.subDivisions;

  useNonUsCountryDefaultValues({
    isUS,
    countriesData,
    customerCountry: country,
    customerState: state,
    defaultValues: defaultValues as CombinedAddressForm,
    setDefaultValues,
    resetField,
  });

  useSelectedCountryEffects({
    selectedCountry,
    country,
    state,
    postalCode,
    city,
    resetField,
    clearErrors,
    setValue,
    formMethods,
  });
  useValidateAutoComplete({
    formMethods,
    relevantFields: [
      OnboardingFormFields.CITY,
      OnboardingFormFields.STATE,
      OnboardingFormFields.ZIP,
      OnboardingFormFields.LINE1,
      OnboardingFormFields.APARTMENT,
    ],
    isDisableValidateAutoComplete,
    setIsDisableValidateAutoComplete,
  });

  const states = subDivisions?.map((subDivision) => ({
    title: subDivision.name,
    value: subDivision.code,
  }));

  return {
    handleSubmit,
    formMethods,
    isPending: isPending || isCompletingOnboarding,
    countries,
    selectedCountry,
    states,
    isUS,
  };
};
